import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import {
    useIsAuthenticated,
    useMsal,
    useMsalAuthentication,
} from '@azure/msal-react';
import {
    Alert,
    Box,
    CircularProgress,
    Container,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import WorkspaceLogo from '~/components/Logo/WorkspaceLogo';
import AppConfig from '~/utils/appconfig';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const isAuthenticated = useIsAuthenticated();
    const { inProgress } = useMsal();
    const [errorMsg, setErrorMsg] = useState<string>();
    const [hasTriedLogin, setHasTriedLogin] = useState(false);
    const request = {
        scopes: ['openid', 'profile', 'email'],
    };
    const { login, error } = useMsalAuthentication(
        InteractionType.Silent,
        request
    );

    useEffect(() => {
        if (
            !isAuthenticated &&
            error &&
            inProgress === InteractionStatus.None &&
            !hasTriedLogin
        ) {
            try {
                login(InteractionType.Redirect, request);
                setHasTriedLogin(true);
                setErrorMsg(undefined);
            } catch (error) {
                setErrorMsg('Login failed. Please try again.');
                setHasTriedLogin(false);
            }
        }
    }, [error, inProgress, isAuthenticated, login, request, hasTriedLogin]);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleRetry = () => {
        setErrorMsg(undefined);
        setHasTriedLogin(false); // Trigger a new login attempt
    };

    return (
        <Container maxWidth="sm">
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                textAlign="center"
                gap={4}
            >
                <WorkspaceLogo height={120} />
                <CircularProgress size={40} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {inProgress !== InteractionStatus.Logout ? (
                        <FormattedMessage
                            id="signingIn"
                            defaultMessage="Signing in..."
                        />
                    ) : (
                        <FormattedMessage
                            id="signingOut"
                            defaultMessage="Signing out..."
                        />
                    )}
                </Typography>

                <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 4, opacity: 0.7 }}
                >
                    <FormattedMessage
                        id="versionInfo"
                        defaultMessage="Version {version}"
                        values={{ version: AppConfig.version }}
                    />
                </Typography>

                {errorMsg && (
                    <Box
                        position="absolute"
                        bottom={20}
                        width="100%"
                        textAlign="center"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
                            {errorMsg}
                        </Alert>
                        <Typography
                            variant="body2"
                            color="primary"
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                            }}
                            onClick={handleRetry}
                        >
                            <FormattedMessage
                                id="retry"
                                defaultMessage="Try again"
                            />
                        </Typography>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default LoginPage;
